/**
 * ValidationErrorDisplay Component
 *
 * Displays inline validation errors for form fields
 */

import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ValidationError } from "utils/validation/calculationValidation";

interface ValidationErrorDisplayProps {
  /**
   * Error message to display
   */
  error?: string;

  /**
   * Full error object
   */
  errorObj?: ValidationError;

  /**
   * Additional CSS class name
   */
  className?: string;
}

const ErrorText = styled.div<{ severity?: "error" | "warning" }>`
  color: ${(props) => (props.severity === "warning" ? "#f59e0b" : "#dc2626")};
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const ErrorIcon = styled.span<{ severity?: "error" | "warning" }>`
  color: ${(props) => (props.severity === "warning" ? "#f59e0b" : "#dc2626")};
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
  margin-top: -1px;
`;

/**
 * Display inline validation error for a form field
 */
export const ValidationErrorDisplay: React.FC<ValidationErrorDisplayProps> = ({
  error,
  errorObj,
  className,
}) => {
  const { t } = useTranslation();
  const message = error || errorObj?.message;
  const severity = errorObj?.severity || "error";

  if (!message) return null;

  // Translate the message (if it's a key, it will be translated; otherwise it will be returned as-is)
  const translatedMessage = t(message, message);

  return (
    <ErrorText severity={severity} className={className} role="alert">
      <ErrorIcon severity={severity}>
        {severity === "warning" ? "⚠" : "✕"}
      </ErrorIcon>
      <span>{translatedMessage}</span>
    </ErrorText>
  );
};

export default ValidationErrorDisplay;
